import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { api, updateAccessTokenExpiry } from '../../utils/api';
import { capitalize } from '../../utils/userUtils';
import { useNavigate } from 'react-router-dom';
import useCurrentUser from '../../hooks/useCurrentUser';
import { IS_DEV_QA } from '../../utils/envSettings';
import i18next from 'i18next';

import { Trans as T, useTranslation } from 'react-i18next';
import PricingTiers from '../Pricing/PricingTiers';
import Button from '../../common/Button/Button';
import Form from 'react-bootstrap/Form';

import { ReactComponent as CheckMarkIcon } from "../../assets/icons_svg/check-mark-outline.svg";
import './Onboarding.scss';

/* PLANS

mvp_free_plan:
-> name_business
-> category_type (new | preset)
-> name_category | preset_category

mvp_choose_plan:
-> verify_email (if !OTP)
-> name_business
-> choose_plan

*/

// TODO
// [ ] verify email has no "back button"

const PLANS = ['mvp_free_plan', 'mvp_choose_plan'];
const BARE_LAYOUT = ['choose_plan'];

const getCurrentStep = onboarding => {
  switch (onboarding.flow) {
    case 'mvp_free_plan':
      switch (onboarding.step) {
        case 'name_business':
          return 1;

        case 'category_type':
          return 2;
          
        case 'name_category':
        case 'preset_category':
          return 3;
      }
  }
}

const getMaxSteps = onboarding => {
  switch (onboarding.flow) {
    case 'mvp_free_plan':
      return 3;
  }
}

export default function Onboarding() {
  const navigate = useNavigate();

  const { currentUser, updateOnboarding } = useCurrentUser();

  const totalSteps = 3;
  const [step, setStep] = useState(currentUser.onboarding.step);
  const [flow, setFlow] = useState(currentUser.onboarding.flow);
  const [stepCount, setStepCount] = useState(getCurrentStep(currentUser.onboarding));
  const [processing, setProcessing] = useState(false);
  
  const [verificationCode, setVerificationCode] = useState("");
  const [businessName, setBusinessName] = useState(currentUser.onboarding.business_name || "");
  const [categoryType, setCategoryType] = useState("new");
  const [categoryName, setCategoryName] = useState("");

  const lastStep = getMaxSteps(currentUser.onboarding);

  const { t } = useTranslation();

  // when the user is updated in the context (using updateUser), this will automatically redirect:
  useEffect(() => {
    if (currentUser.onboarding?.done) {
      navigate("/");
    }
  }, [currentUser, navigate]);

  const onChangeFlow = e => {
    const flow = e.target.value;
    setFlow(flow);
    setStepCount(1);

    switch (flow) {
      case 'mvp_free_plan':
        setStep('name_business');
        break;

      case 'mvp_choose_plan':
        setStep("verify_email");
        break;

      default:
    }
  }

  const skipOnboarding = async () => {
    await updateOnboarding({ done: true });
  }

  const renderSteps = () => {
    const content = [];

    for (let i = 0; i < totalSteps; ++i) {
      content.push(<div key={i} className={classNames('step-marker', { complete: i < stepCount })} />);
    }

    return content;
  }

  const placeholderBusinessName = t("{{name}}'s Business", { name: capitalize(currentUser.first_name) });
  const placeholderCategoryName = t("{{name}}'s Leads", { name: capitalize(currentUser.first_name) });

  const renderContent = () => {
    switch (step) {
      case 'verify_email':
        return (
          <>
            <h2>Verify your email</h2>
            <p className='description'>Enter the six-digit code that we sent to your email <strong>andres@preciousleads.com</strong></p>
            <Form.Control className='verification-code' autoComplete='off' value={verificationCode} onChange={e => setVerificationCode(e.target.value)} />
          </>
        );

      case 'name_business':
        return (
          <>
            <h2><T>Name your business</T></h2>
            <p className='enter-name-label'><T>Enter a name for your business. You can edit it later in your Business Settings.</T></p>
            <Form.Control value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder={placeholderBusinessName} />
          </>
        );
      case 'category_type':
        return (
          <>
            <h2><T>Create your first category</T></h2>
            <p className='enter-name-label'><T>Categories are used to classify your leads, e.g. <strong>Roofing</strong> or <strong>Dental Implants</strong>. All plans include unlimited categories.</T></p>
            <button className={classNames('category-selector', { selected: categoryType === 'new' })} onClick={() => setCategoryType('new')}>
              { categoryType === 'new' ? <CheckMarkIcon /> : <div className='empty-circle' /> }
              <div>
                <h3><T>Start with an empty category</T></h3>
                <p><T>Manually add or import your leads.</T></p>
              </div>
            </button>

            <button className={classNames('category-selector', { selected: categoryType === 'sample' })} onClick={() => setCategoryType('sample')}>
              { categoryType === 'sample' ? <CheckMarkIcon /> : <div className='empty-circle' /> }
              <div>
                <h3><T>Play with a sample category</T></h3>
                <p><T>Add a few dummy leads to test out the look and feel of Precious Leads.</T></p>
              </div>
            </button>
          </>
        );

        case 'name_category':
          return (
            <>
              <div className='check-complete'>
                <CheckMarkIcon />
                <h2><T>Setup complete!</T></h2>
              </div>
              <p className='enter-name-label'><T>Enter a name for your new category. You can edit it later inside the Category’s Settings:</T></p>
              <Form.Control value={categoryName} onChange={e => setCategoryName(e.target.value)} placeholder={placeholderCategoryName} />
            </>
          );
        
        case 'preset_category':
          return (
            <>
              <div className='check-complete'>
                <CheckMarkIcon />
                <h2><T>Setup complete!</T></h2>
              </div>
              <p className='description'><T>We will create a sample category with a few leads so that you can try and test out Precious Leads.</T></p>
            </>
          );

        case 'choose_plan':
          return (
            <>
              <h2>Choose your plan</h2>
              <PricingTiers onClickPlan={onContinue} />
            </>
          );
    }
  }

  const saveStep = newStep => {
    setStep(newStep);

    const data = { step: newStep };

    if (step === 'name_business') {
      data.business_name = businessName.trim() || placeholderBusinessName;
    }

    updateOnboarding(data);
  }

  const createOrganization = async () => {
    const { data } = await api.post('/organizations/', {
      name     : capitalize(currentUser.first_name) + "'s Organization",
      language : i18next.language,
    });

    return data;
  }

  const createBusiness = async (organization_id, create_sample_category = false) => {
    const { data } = await api.post('/businesses/', {
      name     : businessName || placeholderBusinessName,
      language : i18next.language,
      organization_id,
      create_sample_category,
    });

    return data;
  }

  const createSampleCategory = async () => {
    setProcessing(true);
    const org = await createOrganization();
    const business = await createBusiness(org.id, true);

    updateAccessTokenExpiry(365);

    // navigation will autoatically change by updating the currentUser in the context:
    await updateOnboarding({
      show_welcome    : true,
      done            : true,
      new_category_id : business.sample_category_id,
    })
  }

  const createNewCategory = async () => {
    setProcessing(true);
    const org = await createOrganization();
    const business = await createBusiness(org.id);

    await api.post('/categories/', {
      name        : categoryName || placeholderCategoryName,
      business_id : business.id,
      language    : i18next.language,
    });

    updateAccessTokenExpiry(365);

    // navigation will autoatically change by updating the currentUser in the context:
    await updateOnboarding({ show_welcome: true, done: true });
    
  }

  const onContinue = () => {
    lastStep !== stepCount && setStepCount(stepCount + 1);

    switch (flow) {
      case 'mvp_free_plan':
        switch (step) {
          case 'name_business':
            return saveStep('category_type');

          case 'category_type':
            return saveStep(categoryType === 'new' ? 'name_category' : 'preset_category');

          case 'preset_category':
            return createSampleCategory();

          case 'name_category':
            return createNewCategory();
        }

      case 'mvp_choose_plan':
        switch (step) {
          case 'verify_email':
            return setStep('name_business');

          case 'name_business':
            return setStep('choose_plan');

          case 'choose_plan':
            return setStep('category_type');

          case 'category_type':
            return setStep(categoryType === 'new' ? 'name_category' : 'preset_category');
        }
    }
  }

  const onBack = () => {
    setStepCount(stepCount - 1);

    switch (flow) {
      case 'mvp_free_plan':
        switch (step) {
          case 'choose_category':
            return saveStep('name_business');

          case 'category_type':
            return saveStep('name_business');

          case 'name_category':
          case 'preset_category':
            return saveStep('category_type');
        }

      case 'mvp_choose_plan':
        switch (step) {
          case 'name_business':
        }
    }
  }

  return (
    <main className="__onboarding">
      <h1>Precious Leads</h1>

      { BARE_LAYOUT.includes(step)
      ? <div className={`step-${step}`}>{ renderContent() }</div>
      : (
        <section className='main-content'>
          <div className='step-container'>{ renderSteps() }</div>
          { renderContent() }
          <div className='actions'>
            <Button variant='primary' onClick={onContinue} loading={processing}>{ stepCount === lastStep ? <T>Get Started</T> : <T>Next</T> }</Button>
            { stepCount !== 1 && <Button variant='secondary' onClick={onBack} disabled={processing}><T>Back</T></Button> }
          </div>
        </section>
      )}

      { IS_DEV_QA && <Button variant='primary' style={{ position: "fixed", bottom: 72 }} onClick={skipOnboarding}>Skip</Button> }

      <Form.Select value={flow} onChange={onChangeFlow} className='plan-selector'>
        { PLANS.map(plan => <option key={plan} value={plan}>{ plan }</option> )}
      </Form.Select>
    </main>
  )
}
